import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  TrendingUp, 
  TrendingDown, 
  CreditCard, 
  Layers,
  Sparkles,
  CheckCircle2,
  Info
} from 'lucide-react';

export interface TransactionStats {
  totalTransactions: number;
  totalGroups: number;
  revenueTransactions: number;
  expenseTransactions: number;
  revenueAmount: number;
  expenseAmount: number;
  accounts: string[];
  accountDetails: Record<string, { name: string; count: number }>;
}

interface BankAccountSummaryDisplayProps {
  stats: TransactionStats;
  showGuidelines?: boolean;
}

export const BankAccountSummaryDisplay = ({ stats, showGuidelines = true }: BankAccountSummaryDisplayProps) => {
  return (
    <div className="space-y-6">
      {/* Prominent Header Banner */}
      <Card className="border-2 border-primary bg-gradient-to-r from-primary/10 to-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">Transaction Training Summary</h2>
              <p className="text-muted-foreground">
                Before you begin training, review the insights below and follow the training guidelines to efficiently categorize your transactions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header */}
      <div>
        <h3 className="text-xl font-semibold mb-2">Ready to Map Your Transactions</h3>
        <p className="text-muted-foreground">
          We've analyzed your transactions and identified patterns. Review the summary below, then proceed to map them to categories.
        </p>
      </div>

      {/* Stats Cards - Summary Data */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalTransactions.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.totalGroups} unique patterns identified
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600">
              ${stats.revenueAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.revenueTransactions} transaction{stats.revenueTransactions !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-600" />
              Expenses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">
              ${stats.expenseAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.expenseTransactions} transaction{stats.expenseTransactions !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Transaction Breakdown
          </CardTitle>
          <CardDescription>
            Detailed view of your transaction data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Accounts */}
          {stats.accounts.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Accounts ({stats.accounts.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {stats.accounts.map(accId => (
                  <div key={accId} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                    <span className="text-sm">{stats.accountDetails[accId].name}</span>
                    <Badge variant="outline">
                      {stats.accountDetails[accId].count} transactions
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pattern Summary */}
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Pattern Analysis
            </h4>
            <div className="p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Unique patterns identified</span>
                <span className="font-semibold">{stats.totalGroups}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Average transactions per pattern</span>
                <span className="font-semibold">
                  {stats.totalGroups > 0 
                    ? (stats.totalTransactions / stats.totalGroups).toFixed(1)
                    : '0'}
                </span>
              </div>
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  <CheckCircle2 className="h-3 w-3 inline mr-1" />
                  By mapping just {stats.totalGroups} patterns, you'll categorize all {stats.totalTransactions} transactions!
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Training Guidelines Card - Prominent */}
      {showGuidelines && (
        <Card className="border-2 border-primary/20 bg-gradient-to-br from-blue-50 to-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-primary" />
              Training Guidelines & Instructions
            </CardTitle>
            <CardDescription>
              Follow these steps to efficiently train the AI on your transaction patterns
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  1
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Review Transaction Patterns</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    We've automatically grouped {stats.totalGroups} similar transaction patterns from your {stats.totalTransactions} transactions. Each pattern represents transactions with similar descriptions that can be categorized together.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  2
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Map Each Pattern Once</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    When you categorize one transaction from a pattern, we'll automatically apply that categorization to all similar transactions in that group. This saves you significant time!
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  3
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Use AI Suggestions</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Our AI will analyze each transaction and suggest the most likely category, subcategory, and detail source. Review and accept suggestions when they're correct, or adjust as needed.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  4
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Start with Largest Groups</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Focus on the transaction patterns with the most transactions first. These represent your most common expenses/revenue and will have the biggest impact when categorized.
                  </p>
                </div>
              </div>
            </div>
            
            <Alert className="bg-amber-50 border-amber-200 mt-4">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-xs">
                <strong>Pro Tip:</strong> You can skip patterns you're unsure about and come back to them later. The system will remember your progress.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
